import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subscription, map, take } from 'rxjs';
import { Store } from '@ngrx/store';

import {
  Category,
  FinnairCart,
  FinnairItineraryItemFlight,
  FinnairOrder,
  FinnairPassengerItem,
  FinnairServiceItem,
} from '@fcom/dapi/api/models';
import {
  ElementActions,
  ElementTypes,
  GtmPurchaseFlow,
  GtmTripType,
  MultivariateTestId,
  TestVariant,
} from '@fcom/common/interfaces';
import { Amount, LocationInfoMap, ServiceCatalogSelectionsForAllTravelers } from '@fcom/dapi/interfaces';
import { unsubscribe } from '@fcom/core/utils';
import { ButtonPosition, ButtonTheme, ModalButtons } from '@fcom/ui-components';
import { BookingAppState } from '@fcom/common/interfaces/booking';
import { GtmService } from '@fcom/common/gtm';
import { finShare } from '@fcom/rx';
import { SeatMapStatus } from '@fcom/common/interfaces/seat-map.interface';
import { MultivariateTestService } from '@fcom/common/multivariate-test/services/multivariate-test.service';

import { EligibilityWithCategory, FlightWithStreams, ItineraryModal, SeatChangeEvent } from '../../../interfaces';
import { SeatMapActions, seatSelections, ServicesActions } from '../../../../../store';
import { calculateSubTotalPrice, getSeatMapFlightInfoFromCart } from '../../../utils';
import { getBoundDetailsModalData } from '../../../../../utils/ancillaries.utils';
import { AncillaryLoaderStatus } from '../../../enums';

@Component({
  selector: 'fin-seat-pre-selection',
  templateUrl: './seat-pre-selection.component.html',
  styleUrls: ['./seat-pre-selection.component.scss'],
  standalone: false,
})
export class SeatPreSelectionComponent implements OnInit, OnDestroy {
  readonly Category = Category;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonPosition = ButtonPosition;
  readonly ModalButtons = ModalButtons;
  readonly SeatMapStatus = SeatMapStatus;

  @Input() flights$: Observable<FlightWithStreams[]>;
  @Input() travelers$: Observable<FinnairPassengerItem[]>;
  @Input() tripType$: Observable<GtmTripType>;
  @Input() purchaseFlow$: Observable<GtmPurchaseFlow>;
  @Input() locations: LocationInfoMap = {};
  @Input()
  flightAncillaryAvailability$: Observable<Record<string, EligibilityWithCategory[]>>;
  @Input() cartOrOrder$: Observable<FinnairCart | FinnairOrder>;
  @Input()
  passengersWithPetcIncompatibleSeats$: Observable<Record<string, string[]>> = of({});
  @Input() seatSelections$: Observable<ServiceCatalogSelectionsForAllTravelers>;
  @Input() combinedSeats: FinnairServiceItem | null;
  @Input() cartTotalPrice$: Observable<Amount>;
  @Input() selectionLoading$: Observable<boolean>;

  @Output() preSelectClosed = new EventEmitter<boolean>();
  @Output() flightSelected = new EventEmitter<FinnairItineraryItemFlight>();
  @Output() seatChanged = new EventEmitter<SeatChangeEvent>();

  slideIndex$ = new BehaviorSubject(0);
  isSideBarOpen = false;
  itineraryModalClosed$: EventEmitter<boolean> = new EventEmitter();
  subTotalPrice$: Observable<Amount | null>;
  hasSelections$: Observable<boolean>;
  totalPrice$: Observable<Amount>;
  subscriptions: Subscription = new Subscription();
  itineraryModal: ItineraryModal = {
    open: false,
    data: null,
  };
  translationKey$: BehaviorSubject<{ key?: string; params?: Record<string, string | number> }> = new BehaviorSubject(
    {}
  );
  passengers$: Observable<FinnairPassengerItem[]>;
  showFlightCard$: Observable<boolean> = of(true);
  ancillaryLoader$: BehaviorSubject<AncillaryLoaderStatus> = new BehaviorSubject(AncillaryLoaderStatus.OFF);
  seatMapPreloadingEnabled$: Observable<boolean> = of(false);
  private subtotalPriceSub$: ReplaySubject<Amount & { category: Category; fragmentId: string }> = new ReplaySubject<
    Amount & { category: Category; fragmentId: string }
  >(1);
  skipSummary$: Observable<boolean> = of(false);

  constructor(
    private store$: Store<BookingAppState>,
    private gtmService: GtmService,
    private multivariateTestService: MultivariateTestService
  ) {}

  ngOnInit(): void {
    this.skipSummary$ = this.multivariateTestService.getTestVariant(MultivariateTestId.SKIP_SUMMARY).pipe(
      map(({ variant }) => variant === TestVariant.B),
      finShare()
    );
    this.seatMapPreloadingEnabled$ = this.cartOrOrder$.pipe(
      map((cartOrOrder) => cartOrOrder.otherInformation.useSeatmapPrefetch),
      finShare()
    );
    this.passengers$ = this.cartOrOrder$.pipe(map((cartOrOrder) => cartOrOrder.passengers));

    this.subTotalPrice$ = calculateSubTotalPrice(this.subtotalPriceSub$, of(Category.SEAT));

    this.totalPrice$ = combineLatest([this.cartTotalPrice$, this.subTotalPrice$]).pipe(
      map(([totalPrice, subTotalPrice]) => ({
        amount: String(Number(totalPrice.amount) + Number(subTotalPrice.amount)),
        currencyCode: totalPrice.currencyCode,
      }))
    );

    this.hasSelections$ = this.seatSelections$.pipe(
      map((selections) => Object.values(selections).some((selection) => selection.some((s) => s.seatNumber))),
      finShare()
    );

    this.subscriptions.add(
      combineLatest([this.slideIndex$, this.flights$])
        .pipe(
          map(([slideIndex, flights]) => {
            return flights[slideIndex];
          })
        )
        .subscribe((currentFlight) => {
          this.isSideBarOpen = false;
          this.flightSelected.emit(currentFlight);
          this.updateSubTotal(currentFlight.id);
        })
    );

    this.subscriptions.add(
      combineLatest([this.slideIndex$, this.flights$, this.hasSelections$]).subscribe(
        ([slideIndex, flights, hasSelections]) => {
          const isLast = slideIndex === flights.length - 1;

          this.setTitle(isLast, hasSelections);
        }
      )
    );
  }

  subTotalChange(amount: Amount, fragmentId: string): void {
    this.subtotalPriceSub$.next({ ...amount, fragmentId, category: Category.SEAT });
  }

  continue(): void {
    this.subscriptions.add(
      combineLatest([this.slideIndex$, this.flights$, this.hasSelections$, this.skipSummary$])
        .pipe(take(1))
        .subscribe(([slideIndex, flights, hasSelections, skipSummary]) => {
          const isLast = slideIndex === flights.length - 1;

          if (hasSelections) {
            this.trackEvent('continue-selection');
          }

          if (!isLast) {
            this.slideIndex$.next(slideIndex + 1);
          } else if (hasSelections && !skipSummary) {
            this.isSideBarOpen = true;
          } else {
            this.confirmSelection();
          }

          this.trackEvent('skip-selection-bottom', 'bottom');
        })
    );
  }

  confirmSelection(): void {
    this.trackEvent('confirm-selection');
    this.isSideBarOpen = false;
    // setTimeout so that sidebar is closed properly before this component is destroyed
    setTimeout(() => {
      this.preSelectClosed.emit(true);
    });
  }

  skipSeatSelection(trackElementState: string): void {
    this.trackEvent(`skip-selection-${trackElementState}`, trackElementState);
    // Clear cart only if user has made seat selection to avoid unnecessary back-end calls
    this.subscriptions.add(
      this.hasSelections$.pipe(take(1)).subscribe((hasSelections) => {
        if (hasSelections) {
          this.store$.dispatch(SeatMapActions.clearSelections());
          this.store$.dispatch(ServicesActions.clearBundleSelectionsForCategory({ category: Category.SEAT }));
        }
        this.preSelectClosed.emit(hasSelections);
      })
    );
  }

  changeSeats(e: SeatChangeEvent): void {
    this.seatChanged.emit(e);
    this.updateSubTotal(e.flightId);
  }

  setTitle(isLast: boolean, hasSelections: boolean): void {
    if (hasSelections) {
      if (isLast) {
        this.translationKey$.next({ key: 'actions.next' });
      } else {
        this.translationKey$.next({ key: 'ancillaries.seats.continueToFlight' });
      }
    } else if (isLast) {
      this.translationKey$.next({ key: 'ancillaries.seats.skipSeatSelection' });
    } else {
      this.translationKey$.next({ key: 'ancillaries.seats.skipFlight' });
    }
  }

  viewDetails(): void {
    this.itineraryModal = getBoundDetailsModalData(this.cartOrOrder$, this.slideIndex$);
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  updateSubTotal(flightId: string): void {
    this.subscriptions.add(
      this.store$.pipe(seatSelections(true), take(1)).subscribe((allSeatSelections) => {
        const priceAndCurrencyCode = (
          allSeatSelections && allSeatSelections[flightId] ? allSeatSelections[flightId] : []
        )
          .filter((seatSelection) => seatSelection.variant && seatSelection.pricePerItem && !seatSelection.paid)
          .reduce(
            (combined, currentSeatSelection) => {
              return {
                price: combined.price + Number(currentSeatSelection.pricePerItem.amount ?? 0),
                currencyCode: combined.currencyCode ?? currentSeatSelection.pricePerItem.currencyCode,
              };
            },
            { price: 0, currencyCode: undefined }
          );

        const amount = {
          amount: String(priceAndCurrencyCode?.price),
          currencyCode: priceAndCurrencyCode?.currencyCode,
        };

        this.subtotalPriceSub$.next({ ...amount, fragmentId: flightId, category: Category.SEAT });
      })
    );
  }

  private trackEvent(elementName: string, state?: string): void {
    this.subscriptions.add(
      this.cartOrOrder$.pipe(take(1)).subscribe((cart) => {
        this.gtmService.seatMapEvent(elementName, ElementTypes.BUTTON, state, ElementActions.CLICK, {
          flightInfo: getSeatMapFlightInfoFromCart(cart),
        });
      })
    );
  }

  /**
   * This is a performance improvement and stops child components being destroyed and recreated when the flights array changes.
   */
  flightsTrackBy(_index: number, flight: FlightWithStreams): string {
    return flight.id;
  }
}
